export function hasCsvInjection(input) {
    // Regular expression to match dangerous patterns at the start of a field
    const dangerousPatternRegex = /^[=+\-@]/;

    // Split input into fields based on common delimiters in CSV
    const delimiters = [",", ";", " ", "\t"]; // Include common delimiters
    let insideQuotes = false;
    let startOfField = true;

    // Function to trim and check field for dangerous pattern
    const checkField = field => {
        return dangerousPatternRegex.test(field.trim());
    };

    const fields = [];
    let currentField = "";

    for (let i = 0; i < input?.length; i += 1) {
        const char = input[i];

        if (char === '"') {
            insideQuotes = !insideQuotes;
        }

        if (!insideQuotes && delimiters.includes(char)) {
            fields.push(currentField.trim());
            currentField = "";
            startOfField = true;
        } else {
            currentField += char;
            if (startOfField) {
                startOfField = false;
            }
        }
    }

    // Push the last field into fields array
    fields.push(currentField.trim());

    // Check each field for dangerous patterns
    const suspectedField = fields.find(field => checkField(field));

    // Found a match, indicating potential CSV Injection
    return suspectedField?.length > 0;
}

/**
 * @param {string} input
 * @returns Whether input string is a valid comma separated string
 */
export function isAValidCommaSeparatedString(input) {
    const commaSeparatedValueRegex = /((.+),?)*/;
    return !!input.match(commaSeparatedValueRegex);
}

/**
 * @param {string} input
 * @returns Whether each comma separated value is unique
 */
export function areCommaSeparatedValuesUnique(input) {
    const values = input.split(",");
    const uniqueValues = [...new Set(values)];
    return uniqueValues.length === values.length;
}

export const fieldValidationTypeCsv = { type: "csvInjection" };

export const fieldValidationTypeRequired = { type: "required" };

export const fieldValidationTypeCommaSeparatedValue = { type: "commaSeparatedValue" };

export const fieldValidationTypeCommaSeparatedUniqueValue = { type: "commaSeparatedUniqueValue" };

/**
 * Validates a field value based on validation type
 * @param {string} fieldName - Name of the field
 * @param {string} value - Field value to validate
 * @param {string} validationType - Type of validation to perform
 * @returns {string|null} Error message if validation fails, null otherwise
 */
const validateFieldByType = (fieldName, value, validationType) => {
    switch (validationType) {
        case fieldValidationTypeRequired.type:
            return !value.trim().length ? `${fieldName} is required.` : null;

        case fieldValidationTypeCsv.type:
            return hasCsvInjection(value) ? `Remove characters = + - @.` : null;

        case fieldValidationTypeCommaSeparatedValue.type:
            return !isAValidCommaSeparatedString(value)
                ? `Ensure multiple values are comma separated.`
                : null;

        case fieldValidationTypeCommaSeparatedUniqueValue.type:
            return !areCommaSeparatedValuesUnique(value)
                ? `Please enter unique comma separated values.`
                : null;

        default:
            return null;
    }
};
/**
 * Validates a single field based on its validation rules
 * @param {string} fieldName - Name of the field being validated
 * @param {Object} field - Field object containing value and validations
 * @returns {Object} Validation result with status and issues
 */
const validateSingleField = (fieldName, field) => {
    const issues = [];

    field.validations.forEach(validation => {
        const { value } = field;
        const issue = validateFieldByType(fieldName, value, validation.type);
        if (issue) {
            issues.push(issue);
        }
    });

    return {
        status: issues.length > 0 ? "failed" : "passed",
        ...(issues.length > 0 && { issues }),
    };
};

export const fieldsValidation = fields => {
    const results = {};

    Object.keys(fields).forEach(fieldName => {
        const field = fields[fieldName];
        results[fieldName] = validateSingleField(fieldName, field);
    });

    return results;
};
